import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useRef } from 'react';
import Head from '../components/Head';
import { NoData } from '../components/NoData';
import SkeletonLoader from '../components/SkeletonLoader';
import { useActivities } from '../hooks/useActivities';
import styles from '../style.module.css';
import {
  CustomActivity,
  getRidingActivities,
  getRunningActivities,
  getSwimmingActivities,
  parseAllActivities,
} from '../utils/activities';
import { blue, green, orange, titleStyle } from '../utils/common';

const getDayHourArray = (activity: CustomActivity) => [
  activity.day,
  activity.hour,
];

const WeekScatterChart = () => {
  const { activities, isLoading } = useActivities();
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (!activities) {
    return <NoData>No activities</NoData>;
  }

  const allActivities = activities.map(parseAllActivities);

  const [runningActivities, ridingActivities, swimmingActivities] = [
    getRunningActivities(allActivities),
    getRidingActivities(allActivities),
    getSwimmingActivities(allActivities),
  ];

  const runningData = runningActivities.map(getDayHourArray);
  const ridingData = ridingActivities.map(getDayHourArray);
  const swimmingData = swimmingActivities.map(getDayHourArray);

  const options = {
    chart: {
      type: 'scatter',
      zoomType: 'xy', // "x", "y", "xy"
    },
    title: {
      text: 'Répartition des activités sur la semaine',
      style: titleStyle,
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      title: {
        text: 'jour',
      },
      categories: [
        'lundi',
        'mardi',
        'mercredi',
        'jeudi',
        'vendredi',
        'samedi',
        'dimanche',
      ],
      startOnTick: true,
      endOnTick: true,
      showLastLabel: true,
    },
    yAxis: {
      title: {
        text: 'heure',
      },
    },
    legend: {
      enabled: true,
    },
    series: [
      {
        name: 'Cycling',
        id: 'cycling',
        marker: {
          symbol: 'circle',
        },
        data: Object.values(ridingData),
        color: orange,
      },
      {
        name: 'Running',
        id: 'running',
        marker: {
          symbol: 'triangle',
        },
        data: Object.values(runningData),
        color: green,
      },
      {
        name: 'Swimming',
        id: 'swimming',
        marker: {
          symbol: 'square',
        },
        data: Object.values(swimmingData),
        color: blue,
      },
    ],
    plotOptions: {
      scatter: {
        marker: {
          radius: 2.5,
          symbol: 'circle',
          states: {
            hover: {
              enabled: true,
              lineColor: 'rgb(100,100,100)',
            },
          },
        },
        states: {
          hover: {
            marker: {
              enabled: false,
            },
          },
        },
        jitter: {
          x: 0.005,
        },
      },
    },
  };

  return (
    <div id="week">
      <Head title="Strava Dashboard - week" />
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartComponentRef}
        containerProps={{ className: styles.chartContainer }}
      />
    </div>
  );
};

export default WeekScatterChart;
